import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Button, TextField,
  FormControl, InputLabel, Select, MenuItem, Chip, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Tabs, Tab, CircularProgress, Switch, FormControlLabel,
  Autocomplete, Divider
} from '@mui/material';
import {
  TrendingUp, TrendingDown, Refresh as RefreshIcon,
  Download as DownloadIcon, TableChart as TableIcon,
  ShowChart as ChartIcon, CalendarToday as CalendarIcon,
  AccessTime as TimeIcon, Thermostat as TempIcon,
  Opacity as HumidityIcon, Cloud as CO2Icon
} from '@mui/icons-material';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, Title, Tooltip, Legend, TimeScale
} from 'chart.js';
import 'chartjs-adapter-moment';
import zoomPlugin from 'chartjs-plugin-zoom';
import moment from 'moment';
import ModernLayout from './common/ModernLayout';
import { StatsCard } from './common/ModernCard';
import { GradientButton } from './common/ModernButton';
import axiosInstance from './axiosConfig';
import { useTemperatureUnit } from './TemperatureUnitContext';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, BarElement,
  Title, Tooltip, Legend, TimeScale, zoomPlugin
);

const SensorAnalytics = () => {
  const { temperatureUnit, convertTemperature } = useTemperatureUnit();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Data
  const [rooms, setRooms] = useState([]);
  const [sensors, setSensors] = useState([]);
  const [sensorData, setSensorData] = useState([]);
  const [statistics, setStatistics] = useState(null);
  
  // Filters
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedRoom, setSelectedRoom] = useState([]);
  const [selectedSensor, setSelectedSensor] = useState([]);
  const [selectedMetrics, setSelectedMetrics] = useState(['temperature', 'humidity']);
  const [dateRange, setDateRange] = useState({
    from: moment().subtract(7, 'days').format('YYYY-MM-DD'),
    to: moment().format('YYYY-MM-DD')
  });
  const [groupBy, setGroupBy] = useState('hour'); // hour, day, week
  const [showAverage, setShowAverage] = useState(true);
  const [showMinMax, setShowMinMax] = useState(false);
  const [compareMode, setCompareMode] = useState(false);

  // Projects
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchRooms();
    }
  }, [selectedProject]);

  useEffect(() => {
    if (selectedRoom.length > 0) {
      fetchSensors();
    }
  }, [selectedRoom]);

  useEffect(() => {
    if (selectedSensor.length > 0) {
      fetchSensorData();
    }
  }, [selectedSensor, dateRange, groupBy]);

  const fetchProjects = async () => {
    try {
      const response = await axiosInstance.get('/api/projects');
      setProjects(response.data || []);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchRooms = async () => {
    try {
      const response = await axiosInstance.get(`/api/rooms/${selectedProject}`);
      setRooms(response.data || []);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  };

  const fetchSensors = async () => {
    setLoading(true);
    try {
      const allSensors = [];
      for (const roomId of selectedRoom) {
        const response = await axiosInstance.get('/api/registered-sensors', {
          params: { projectId: selectedProject, roomId }
        });
        const sensorsWithRoom = response.data.map(sensor => ({
          ...sensor,
          roomName: rooms.find(r => r.id === roomId)?.name || 'Unknown'
        }));
        allSensors.push(...sensorsWithRoom);
      }
      setSensors(allSensors);
      if (allSensors.length > 0 && selectedSensor.length === 0) {
        setSelectedSensor([allSensors[0].id]);
      }
    } catch (error) {
      console.error('Error fetching sensors:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSensorData = async () => {
    setLoading(true);
    try {
      const allData = [];
      
      for (const sensorId of selectedSensor) {
        const response = await axiosInstance.get(`/api/data/${sensorId}`, {
          params: {
            from: dateRange.from,
            to: dateRange.to
          }
        });
        
        const sensor = sensors.find(s => s.id === sensorId);
        allData.push({
          sensorId,
          sensorName: sensor?.name || `Sensor ${sensorId}`,
          roomName: sensor?.roomName || 'Unknown',
          data: response.data || []
        });
      }
      
      setSensorData(allData);
      calculateStatistics(allData);
    } catch (error) {
      console.error('Error fetching sensor data:', error);
      setSensorData([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateStatistics = (data) => {
    if (!data || data.length === 0) return;

    const stats = {};
    selectedMetrics.forEach(metric => {
      const allValues = data.flatMap(d => 
        d.data.map(reading => parseFloat(reading[metric])).filter(v => !isNaN(v))
      );

      if (allValues.length > 0) {
        stats[metric] = {
          min: Math.min(...allValues),
          max: Math.max(...allValues),
          avg: allValues.reduce((a, b) => a + b, 0) / allValues.length,
          count: allValues.length
        };
      }
    });

    setStatistics(stats);
  };

  const groupDataByPeriod = (data) => {
    if (!data || data.length === 0) return [];

    const grouped = {};

    data.forEach(reading => {
      let key;
      const timestamp = moment(reading.timestamp);
      
      if (groupBy === 'hour') {
        key = timestamp.format('YYYY-MM-DD HH:00');
      } else if (groupBy === 'day') {
        key = timestamp.format('YYYY-MM-DD');
      } else if (groupBy === 'week') {
        key = timestamp.startOf('week').format('YYYY-MM-DD');
      }

      if (!grouped[key]) {
        grouped[key] = { timestamp: key, values: {} };
        selectedMetrics.forEach(metric => {
          grouped[key].values[metric] = [];
        });
      }

      selectedMetrics.forEach(metric => {
        const value = parseFloat(reading[metric]);
        if (!isNaN(value)) {
          grouped[key].values[metric].push(value);
        }
      });
    });

    // Calculate averages
    return Object.values(grouped).map(group => {
      const result = { timestamp: group.timestamp };
      selectedMetrics.forEach(metric => {
        const values = group.values[metric];
        if (values.length > 0) {
          result[`${metric}_avg`] = values.reduce((a, b) => a + b, 0) / values.length;
          result[`${metric}_min`] = Math.min(...values);
          result[`${metric}_max`] = Math.max(...values);
        }
      });
      return result;
    }).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  };

  const prepareChartData = () => {
    if (sensorData.length === 0) return null;

    const datasets = [];
    const colors = {
      temperature: { line: '#FF6384', area: 'rgba(255, 99, 132, 0.2)' },
      humidity: { line: '#36A2EB', area: 'rgba(54, 162, 235, 0.2)' },
      co2: { line: '#FFCE56', area: 'rgba(255, 206, 86, 0.2)' },
      soil_moisture: { line: '#4BC0C0', area: 'rgba(75, 192, 192, 0.2)' }
    };

    sensorData.forEach((sensorDataSet, index) => {
      const groupedData = groupDataByPeriod(sensorDataSet.data);
      
      selectedMetrics.forEach(metric => {
        datasets.push({
          label: `${sensorDataSet.sensorName} - ${metric} (${showAverage ? 'avg' : 'real'})`,
          data: groupedData.map(d => ({
            x: d.timestamp,
            y: metric === 'temperature' && showAverage 
              ? convertTemperature(d[`${metric}_avg`]) 
              : (showAverage ? d[`${metric}_avg`] : d[metric])
          })),
          borderColor: colors[metric]?.line || '#888',
          backgroundColor: colors[metric]?.area || 'rgba(136, 136, 136, 0.2)',
          tension: 0.4,
          fill: true
        });

        if (showMinMax && showAverage) {
          // Min line
          datasets.push({
            label: `${sensorDataSet.sensorName} - ${metric} (min)`,
            data: groupedData.map(d => ({
              x: d.timestamp,
              y: d[`${metric}_min`]
            })),
            borderColor: colors[metric]?.line || '#888',
            backgroundColor: 'transparent',
            borderDash: [5, 5],
            tension: 0.4,
            fill: false,
            borderWidth: 1
          });

          // Max line
          datasets.push({
            label: `${sensorDataSet.sensorName} - ${metric} (max)`,
            data: groupedData.map(d => ({
              x: d.timestamp,
              y: d[`${metric}_max`]
            })),
            borderColor: colors[metric]?.line || '#888',
            backgroundColor: 'transparent',
            borderDash: [5, 5],
            tension: 0.4,
            fill: false,
            borderWidth: 1
          });
        }
      });
    });

    return {
      datasets,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        plugins: {
          legend: {
            position: 'top',
            labels: {
              usePointStyle: true,
              padding: 15
            }
          },
          title: {
            display: true,
            text: `Análisis de Sensores - ${groupBy === 'hour' ? 'Por Hora' : groupBy === 'day' ? 'Por Día' : 'Por Semana'}`
          },
          zoom: {
            zoom: {
              wheel: {
                enabled: true,
              },
              pinch: {
                enabled: true
              },
              mode: 'x',
            },
            pan: {
              enabled: true,
              mode: 'x',
            }
          }
        },
        scales: {
          x: {
            type: 'time',
            time: {
              unit: groupBy === 'hour' ? 'hour' : 'day',
              displayFormats: {
                hour: 'MMM DD HH:mm',
                day: 'MMM DD'
              }
            },
            title: {
              display: true,
              text: 'Tiempo'
            }
          },
          y: {
            title: {
              display: true,
              text: 'Valor'
            }
          }
        }
      }
    };
  };

  const exportToCSV = () => {
    if (sensorData.length === 0) return;

    const headers = ['Timestamp', 'Sensor', 'Room', ...selectedMetrics];
    const rows = sensorData.flatMap(sensorDataSet =>
      sensorDataSet.data.map(reading => [
        moment(reading.timestamp).format('YYYY-MM-DD HH:mm:ss'),
        sensorDataSet.sensorName,
        sensorDataSet.roomName,
        ...selectedMetrics.map(metric => reading[metric] || '-')
      ])
    );

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sensor_analytics_${dateRange.from}_to_${dateRange.to}.csv`;
    link.click();
  };

  const chartData = prepareChartData();

  const renderFilters = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Filtros de Análisis
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Proyecto</InputLabel>
              <Select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                label="Proyecto"
              >
                {projects.map(project => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <Autocomplete
              multiple
              options={rooms}
              getOptionLabel={(option) => option.name}
              value={rooms.filter(r => selectedRoom.includes(r.id))}
              onChange={(e, newValue) => {
                setSelectedRoom(newValue.map(v => v.id));
              }}
              renderInput={(params) => (
                <TextField {...params} label="Rooms" size="small" />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.name}
                    {...getTagProps({ index })}
                    size="small"
                  />
                ))
              }
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Autocomplete
              multiple
              options={sensors}
              getOptionLabel={(option) => `${option.name} (${option.roomName})`}
              value={sensors.filter(s => selectedSensor.includes(s.id))}
              onChange={(e, newValue) => {
                setSelectedSensor(newValue.map(v => v.id));
              }}
              renderInput={(params) => (
                <TextField {...params} label="Sensores" size="small" />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.name}
                    {...getTagProps({ index })}
                    size="small"
                  />
                ))
              }
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Autocomplete
              multiple
              options={['temperature', 'humidity', 'co2', 'soil_moisture']}
              getOptionLabel={(option) => {
                const labels = {
                  temperature: '🌡️ Temperatura',
                  humidity: '💧 Humedad',
                  co2: '☁️ CO2',
                  soil_moisture: '🌱 Humedad Suelo'
                };
                return labels[option];
              }}
              value={selectedMetrics}
              onChange={(e, newValue) => setSelectedMetrics(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Métricas" size="small" />
              )}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Desde"
              value={dateRange.from}
              onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Hasta"
              value={dateRange.to}
              onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Agrupar por</InputLabel>
              <Select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
                label="Agrupar por"
              >
                <MenuItem value="hour">Por Hora</MenuItem>
                <MenuItem value="day">Por Día</MenuItem>
                <MenuItem value="week">Por Semana</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <GradientButton
                fullWidth
                startIcon={<RefreshIcon />}
                onClick={fetchSensorData}
                disabled={loading || selectedSensor.length === 0}
              >
                Actualizar
              </GradientButton>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={
              <Switch
                checked={showAverage}
                onChange={(e) => setShowAverage(e.target.checked)}
                color="primary"
              />
            }
            label="Mostrar Promedio"
          />
          <FormControlLabel
            control={
              <Switch
                checked={showMinMax}
                onChange={(e) => setShowMinMax(e.target.checked)}
                color="primary"
              />
            }
            label="Mostrar Min/Max"
          />
          <FormControlLabel
            control={
              <Switch
                checked={compareMode}
                onChange={(e) => setCompareMode(e.target.checked)}
                color="primary"
              />
            }
            label="Modo Comparación"
          />
        </Box>
      </CardContent>
    </Card>
  );

  const renderStatistics = () => {
    if (!statistics) return null;

    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {selectedMetrics.map(metric => {
          const stat = statistics[metric];
          if (!stat) return null;

          const icons = {
            temperature: <TempIcon />,
            humidity: <HumidityIcon />,
            co2: <CO2Icon />,
            soil_moisture: <HumidityIcon />
          };

          const units = {
            temperature: temperatureUnit === 'Fahrenheit' ? '°F' : '°C',
            humidity: '%',
            co2: 'ppm',
            soil_moisture: '%'
          };

          return (
            <Grid item xs={12} sm={6} md={3} key={metric}>
              <StatsCard
                title={metric.toUpperCase()}
                value={`${stat.avg.toFixed(1)}${units[metric]}`}
                subtitle={`Min: ${stat.min.toFixed(1)} | Max: ${stat.max.toFixed(1)}`}
                icon={icons[metric]}
                color="primary"
              />
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const renderChart = () => {
    if (!chartData || sensorData.length === 0) {
      return (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              Selecciona sensores y rango de fechas para ver el gráfico
            </Typography>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Gráfico de Tendencias
            </Typography>
            <GradientButton
              startIcon={<DownloadIcon />}
              onClick={exportToCSV}
              size="small"
            >
              Exportar CSV
            </GradientButton>
          </Box>
          
          <Box sx={{ height: 500 }}>
            <Line data={chartData} options={chartData.options} />
          </Box>
          
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            💡 Usa scroll del mouse para zoom, arrastra para moverte
          </Typography>
        </CardContent>
      </Card>
    );
  };

  const renderTable = () => {
    if (sensorData.length === 0) return null;

    const allReadings = sensorData.flatMap(sd =>
      sd.data.map(reading => ({
        ...reading,
        sensorName: sd.sensorName,
        roomName: sd.roomName
      }))
    ).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Datos Detallados ({allReadings.length} registros)
          </Typography>
          
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Sensor</TableCell>
                  <TableCell>Room</TableCell>
                  {selectedMetrics.includes('temperature') && <TableCell>Temp</TableCell>}
                  {selectedMetrics.includes('humidity') && <TableCell>Humedad</TableCell>}
                  {selectedMetrics.includes('co2') && <TableCell>CO2</TableCell>}
                  {selectedMetrics.includes('soil_moisture') && <TableCell>Soil</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {allReadings.slice(0, 500).map((reading, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      {moment(reading.timestamp).format('YYYY-MM-DD HH:mm:ss')}
                    </TableCell>
                    <TableCell>{reading.sensorName}</TableCell>
                    <TableCell>{reading.roomName}</TableCell>
                    {selectedMetrics.includes('temperature') && (
                      <TableCell>
                        {convertTemperature(reading.temperature)}{temperatureUnit === 'Fahrenheit' ? '°F' : '°C'}
                      </TableCell>
                    )}
                    {selectedMetrics.includes('humidity') && (
                      <TableCell>{reading.humidity}%</TableCell>
                    )}
                    {selectedMetrics.includes('co2') && (
                      <TableCell>{reading.co2} ppm</TableCell>
                    )}
                    {selectedMetrics.includes('soil_moisture') && (
                      <TableCell>{reading.soil_moisture}%</TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {allReadings.length > 500 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Mostrando primeros 500 de {allReadings.length} registros. Exporta a CSV para ver todos.
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderComparison = () => {
    if (!compareMode || sensorData.length < 2) {
      return (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              Selecciona al menos 2 sensores para comparar
            </Typography>
          </CardContent>
        </Card>
      );
    }

    return (
      <Grid container spacing={3}>
        {sensorData.map((sd, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  {sd.sensorName} ({sd.roomName})
                </Typography>
                
                {selectedMetrics.map(metric => {
                  const values = sd.data.map(d => parseFloat(d[metric])).filter(v => !isNaN(v));
                  if (values.length === 0) return null;

                  const avg = values.reduce((a, b) => a + b, 0) / values.length;
                  const min = Math.min(...values);
                  const max = Math.max(...values);

                  return (
                    <Box key={metric} sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {metric.toUpperCase()}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">Promedio</Typography>
                          <Typography variant="h6">{avg.toFixed(1)}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">Mínimo</Typography>
                          <Typography variant="h6" color="info.main">{min.toFixed(1)}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">Máximo</Typography>
                          <Typography variant="h6" color="error.main">{max.toFixed(1)}</Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  );
                })}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <ModernLayout title="Análisis de Sensores">
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              📊 Análisis de Sensores
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Análisis avanzado de datos de temperatura, humedad, CO2 y más
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<CalendarIcon />}
              onClick={() => {
                setDateRange({
                  from: moment().subtract(24, 'hours').format('YYYY-MM-DD'),
                  to: moment().format('YYYY-MM-DD')
                });
              }}
            >
              Últimas 24h
            </Button>
            <Button
              variant="outlined"
              startIcon={<CalendarIcon />}
              onClick={() => {
                setDateRange({
                  from: moment().subtract(7, 'days').format('YYYY-MM-DD'),
                  to: moment().format('YYYY-MM-DD')
                });
              }}
            >
              Última Semana
            </Button>
            <Button
              variant="outlined"
              startIcon={<CalendarIcon />}
              onClick={() => {
                setDateRange({
                  from: moment().subtract(30, 'days').format('YYYY-MM-DD'),
                  to: moment().format('YYYY-MM-DD')
                });
              }}
            >
              Último Mes
            </Button>
          </Box>
        </Box>

        {renderFilters()}

        {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />}

        {!loading && statistics && renderStatistics()}

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            <Tab icon={<ChartIcon />} label="Gráfico" iconPosition="start" />
            <Tab icon={<TableIcon />} label="Tabla de Datos" iconPosition="start" />
            {compareMode && <Tab icon={<TrendingUp />} label="Comparación" iconPosition="start" />}
          </Tabs>
        </Box>

        {activeTab === 0 && renderChart()}
        {activeTab === 1 && renderTable()}
        {activeTab === 2 && compareMode && renderComparison()}
      </Box>
    </ModernLayout>
  );
};

export default SensorAnalytics;

